import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as DOMPurify from "dompurify";
import { i18n } from "next-i18next";
import theme from "../../../../theme";

// import LogoDBW from "/assets/images/Logo_DBW.png";
import LogoDBW from "/public/images/Logo_DBW.png";

/** Fonts */
import "../../jsPDF_fonts/OpenSans/OpenSans-Bold-bold";
import "../../jsPDF_fonts/OpenSans/OpenSans-BoldItalic-bolditalic";
import "../../jsPDF_fonts/OpenSans/OpenSans-ExtraBold-bold";
import "../../jsPDF_fonts/OpenSans/OpenSans-ExtraBoldItalic-bolditalic";
import "../../jsPDF_fonts/OpenSans/OpenSans-Italic-italic";
import "../../jsPDF_fonts/OpenSans/OpenSans-Light-normal";
import "../../jsPDF_fonts/OpenSans/OpenSans-LightItalic-italic";
import "../../jsPDF_fonts/OpenSans/OpenSans-Medium-normal";
import "../../jsPDF_fonts/OpenSans/OpenSans-MediumItalic-italic";
import "../../jsPDF_fonts/OpenSans/OpenSans-Regular-normal";
import "../../jsPDF_fonts/OpenSans/OpenSans-SemiBold-bold";
import "../../jsPDF_fonts/OpenSans/OpenSans-SemiBoldItalic-bolditalic";

const colors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    text: theme.palette.common.black,
};

const fontSizeFactor = 0.95;

const FONT_SIZE = 11.5;
const LINE_HEIGHT = 6;
const MARGINS = { left: 10, right: 15 };
const COLORS = { text: "#000000", link: "#001D77" }; //#0000EE
const ALLOWED_TAGS = [
    { name: "a", newLine: false },
    { name: "b", newLine: false },
    { name: "br", newLine: true },
    { name: "em", newLine: false },
    { name: "li", newLine: true },
    { name: "ol", newLine: true },
    { name: "p", newLine: true },
    { name: "strong", newLine: false },
    { name: "sub", newLine: false },
    { name: "sup", newLine: false },
    { name: "u", newLine: false },
    { name: "ul", newLine: true },
];
const ALLOWED_ATTR = ["href"];

export const makePDF = async ({ pdfOptions, asBlob }) => {
    // const { data, title, language, translationFromJson } = pdfOptions;
    // console.clear();
    process.env.NODE_ENV === "development" && console.log("%c makePDF: ", "background: #666; color: #FFF;");
    process.env.NODE_ENV === "development" && console.log(`%c - pdfOptions: `, "background: #CCC", pdfOptions);

    var doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        putOnlyUsedFonts: true,
        floatPrecision: 16,
        hotfixes: ["px_scaling"],
        compress: true,
    });

    doc.setDocumentProperties({
        title: i18n.t("Metadane"),
        subject: "DBW",
        author: "GUS",
        keywords: "certificate, metryka, DBW, GUS",
        creator: "DBW",
        created: moment().format("DD.MM.YYYY HH:mm:ss"),
        modified: moment().format("DD.MM.YYYY HH:mm:ss"),
        producer: "DBW",
        pdfproducer: "DBW",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 10;

    /** Report logo */
    const logoImage = new Image(LogoDBW.width, LogoDBW.height);
    logoImage.src = LogoDBW.src;
    // doc.addImage(LogoDBW, "PNG", pageWidth / 2 - 320 / 3.78 / 2, 2, 320 / 3.78, 75.95 / 3.78, "logoDBW");
    doc.addImage(logoImage, "PNG", pageWidth / 2 - 320 / 3.78 / 2, 2, 320 / 3.78, 75.95 / 3.78, "logoDBW");
    doc.setLineWidth(1 / 3.78);
    doc.setDrawColor(224, 224, 224);
    doc.line(0, 20, pageWidth - 0, 20);

    const _ids = pdfOptions?.map((o) => o?.data?.id_zmienna)?.join(";");

    pdfOptions.forEach((option, optionIndex) => {
        const { data, title, translationFromJson } = option;
        process.env.NODE_ENV === "development" && console.log(`%c - option: `, "background: #CCC", option);
        if (optionIndex > 0) {
            doc.addPage();
            yPos = 20;
        } else {
            yPos = 35;
        }

        /** Indicator's full name */
        yPos = textify({
            doc,
            text: translationFromJson(data, "name"),
            fontName: "Open Sans",
            fontWeight: 500,
            fontSize: "22.5px",
            y: yPos,
        });

        /** "Nazwa skrócona" section */
        yPos += 8;
        yPos = textify({ doc, text: i18n.t("Nazwa skrócona"), color: "primary", fontSize: "20px", y: yPos });
        yPos = textify({ doc, text: translationFromJson(data, "short_name"), fontWeight: 300, x: 12, y: yPos + 2 });

        /** "Tagi" section */
        yPos += 8;
        yPos = textify({ doc, text: i18n.t("Tagi"), color: "primary", fontSize: "20px", y: yPos });

        const _tags = data.tags
            .sort((a, b) => a.tag_order - b.tag_order)
            .map((tag) => translationFromJson(tag, "tag_name")?.replace(/(<([^>]+)>)|\t|\n/gi, ""))
            .join(", ");

        yPos = textify({
            doc,
            text: _tags,
            color: "primary",
            fontStyle: "italic",
            fontSize: "14px",
            fontWeight: 400,
            x: 12,
            y: yPos + 2,
        });

        /** "Definicja pojęć" section */
        yPos += 8;
        yPos = textify({ doc, text: i18n.t("Definicja pojęć"), color: "primary", fontSize: "20px", y: yPos });
        data.concept_definitions
            .sort((a, b) => a.concept_order - b.concept_order)
            .forEach((concept, i) => {
                yPos = textify({
                    doc,
                    text: `${translationFromJson(concept, "concept_name")?.replace(/(<([^>]+)>)|\t|\n/gi, "")} ${
                        concept.children && concept.children[0]
                            ? translationFromJson(concept.children[0], "child_concept_date_from")
                            : translationFromJson(concept, "concept_date_from")?.replace(/(<([^>]+)>)|\t|\n/gi, "") ||
                              ""
                    }`,
                    fontStyle: concept.children && concept.children[0] ? "italic" : "normal",
                    fontWeight: concept.children && concept.children[0] ? 400 : 700,
                    x: concept.children && concept.children[0] ? 16 : 12,
                    y: yPos + (i === 0 ? 2 : 5),
                });
                if (concept.children?.length) {
                    yPos = textify({
                        doc,
                        text: translationFromJson(concept, "concept_definition"),
                        fontWeight: 300,
                        x: 16,
                        y: yPos + 2,
                    });
                    if (concept?.concept_comment) {
                        yPos = textify({
                            doc,
                            text: translationFromJson(concept, "concept_comment"),
                            fontWeight: 300,
                            x: 16,
                            y: yPos + 2,
                            raw: true,
                        });
                    }
                } else {
                    yPos = textify({
                        doc,
                        text: translationFromJson(concept, "concept_definition"),
                        fontWeight: 300,
                        x: 12,
                        y: yPos + 2,
                    });
                    if (concept?.concept_comment) {
                        yPos = textify({
                            doc,
                            text: translationFromJson(concept, "concept_comment"),
                            fontWeight: 300,
                            x: 12,
                            y: yPos + 2,
                            raw: true,
                        });
                    }
                }
                addLine({ yPos: yPos + 5, doc });
            });

        /** "Wyjaśnienia metodologiczne" section */
        yPos += 8;
        yPos = textify({
            doc,
            text: i18n.t("Wyjaśnienia metodologiczne"),
            color: "primary",
            fontSize: "20px",
            y: yPos,
        });
        if (!data.methodological_notes && (!data?.notes?.length || !Object.keys(data?.notes[0])?.length)) {
            yPos = textify({
                doc,
                text: i18n.t("Brak informacji") + ".",
                fontWeight: 300,
                x: 12,
                y: yPos + 2,
                raw: true,
            });
        }
        if (data.methodological_notes) {
            yPos = textify({
                doc,
                text: translationFromJson(data, "methodological_notes"),
                fontWeight: 300,
                x: 12,
                y: yPos + 2,
                raw: true,
            });
        }
        if (data?.notes?.length) {
            yPos += 2;
            data?.notes.forEach((note, nIdx) => {
                yPos = textify({
                    doc,
                    text: translationFromJson(note, "note_description"),
                    fontWeight: 300,
                    x: 12,
                    y: yPos + 2,
                });
            });
        }

        /** "Dostępne przekroje danych" section */
        yPos += 8;
        yPos = textify({ doc, text: i18n.t("Dostępne przekroje danych"), color: "primary", fontSize: "20px", y: yPos });
        data.available_data_disaggregations
            .sort((a, b) => a.disaggregation_order - b.disaggregation_order)
            .forEach((data, i) => {
                yPos = textify({
                    doc,
                    text: translationFromJson(data, "disaggregation_name"),
                    fontWeight: 300,
                    x: 12,
                    y: yPos + (i === 0 ? 2 : 5),
                });
                yPos = textify({
                    doc,
                    text: `${i18n.t("Częstotliwość aktualizacji danych")}: ${translationFromJson(
                        data,
                        "frequency_name"
                    )?.replace(/(<([^>]+)>)|\t|\n/gi, "")}`,
                    fontWeight: 300,
                    x: 12,
                    y: yPos + 2,
                });
                if (data.last_update) {
                    yPos = textify({
                        doc,
                        text: `${i18n.t("Data ostatniej aktualizacji")}: ${data.last_update}`,
                        fontWeight: 300,
                        x: 12,
                        y: yPos + 2,
                    });
                }
                if (data.next_update) {
                    yPos = textify({
                        doc,
                        text: `${i18n.t("Data kolejnej aktualizacji")}: ${data.next_update}`,
                        fontWeight: 300,
                        x: 12,
                        y: yPos + 2,
                    });
                }
            });

        /** "Źródła danych" section */
        yPos += 8;
        yPos = textify({ doc, text: i18n.t("Źródła danych"), color: "primary", fontSize: "20px", y: yPos });

        data.data_source.forEach((source, id) => {
            yPos = textify({
                doc,
                text: translationFromJson(source, "set_symbol"),
                fontWeight: 300,
                x: 12,
                y: yPos + 2,
            });
            yPos = textify({ doc, text: translationFromJson(source, "set_name"), fontWeight: 300, x: 12, y: yPos + 2 });
            yPos = textify({
                doc,
                text: translationFromJson(source, "gestor_name"),
                fontWeight: 300,
                x: 12,
                y: yPos + 2,
            });
            yPos += 4;
        });

        /** "Tematy badawcze" section */
        yPos += 8;
        yPos = textify({ doc, text: i18n.t("Tematy badawcze"), color: "primary", fontSize: "20px", y: yPos });
        data.surveys_topic.map((topic, i) => {
            yPos = textify({
                doc,
                text: `${topic.survey_symbol} ${translationFromJson(topic, "survey_topic")?.replace(
                    /(<([^>]+)>)|\t|\n/gi,
                    ""
                )}`,
                fontWeight: 300,
                x: 12,
                y: yPos + (i === 0 ? 2 : 5),
            });
            yPos = textify({
                doc,
                text: translationFromJson(topic, "survey_purpose"),
                fontWeight: 300,
                x: 12,
                y: yPos + 2,
            });
        });

        /** "Akty prawne" section */
        if (Object.keys(data.legal_acts[0]).length) {
            yPos += 8;
            yPos = textify({ doc, text: i18n.t("Akty prawne"), color: "primary", fontSize: "20px", y: yPos });
            data.legal_acts
                .sort((a, b) => a.act_order - b.act_order)
                .forEach((act, i) => {
                    yPos = textify({
                        doc,
                        text: translationFromJson(act, "act_title"),
                        fontWeight: 300,
                        x: 12,
                        y: yPos + 0,
                    });
                    yPos = textify({
                        doc,
                        text: translationFromJson(act, "act_adress"),
                        fontWeight: 300,
                        x: 12,
                        y: yPos + (i === 0 ? 2 : 5),
                    });
                });
        }

        /** "Jednostka statystyki publicznej odpowiedzialna za zmienną" section */
        yPos += 8;
        yPos = textify({
            doc,
            text: i18n.t("Jednostka statystyki publicznej odpowiedzialna za zmienną"),
            color: "primary",
            fontSize: "20px",
            y: yPos,
        });
        yPos = textify({
            doc,
            text: translationFromJson(data, "datadisposer_name"),
            fontWeight: 300,
            x: 12,
            y: yPos + 2,
        });
    });

    addFooters(doc);

    if (asBlob) {
        return doc.output("blob");
    } else {
        doc.save(
            `DBW-${i18n.t("metainfo")}-[${_ids}]-${moment().format("YYYYMMDD.HHmm")}${
                i18n.language ? `.${i18n.language?.toLowerCase()}` : ""
            }.pdf`
        );
    }
};

const addFooters = (doc) => {
    const pageCount = doc.getNumberOfPages();
    doc.setFont("Open Sans", "normal");
    doc.setFontSize(8);
    for (var i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(0);
        doc.setFillColor(116, 79, 172);
        doc.rect(0, doc.internal.pageSize.height - 6, doc.internal.pageSize.width, doc.internal.pageSize.height, "F");
        doc.setTextColor(255, 255, 255);
        doc.text(
            String(i) + "/" + String(pageCount),
            doc.internal.pageSize.width - 10,
            doc.internal.pageSize.height - 2,
            {
                align: "right",
            }
        );
        doc.text(
            i18n.t(
                `Wygenerowane z systemu DBW w dn{{dot}} {{date}} r{{dot}} na podstawie ustawień użytkownika{{dot}}`,
                {
                    date: moment().format("DD.MM.YYYY"),
                    dot: ".",
                }
            ),
            10,
            doc.internal.pageSize.height - 2,
            {
                align: "left",
            }
        );
    }
};

const addLine = ({ yPos, doc, test = false }) => {
    if (test) {
        return yPos > doc.internal.pageSize.height - 20;
    }
    if (yPos > doc.internal.pageSize.height - 20) {
        doc.addPage();
        yPos = 10;
    }
    return yPos;
};

const textify = ({
    doc,
    text = "",
    fontSize = "16px",
    fontName = "Open Sans",
    fontStyle = "normal",
    fontWeight = 400,
    textAlign = "left",
    x = 8,
    y,
    color,
    raw = false,
}) => {
    color = color ? colors[color] || color : colors.text;
    const pageWidth = doc.internal.pageSize.getWidth();

    if (!text || typeof text !== "string") return y;

    if (typeof fontSize === "string" && fontSize?.split("px")?.length > 1) {
        fontSize = (Number(fontSize?.split("px")[0]) * 9) / 12;
    }
    fontSize = fontSize * fontSizeFactor;

    if (textAlign === "center") x = pageWidth / 2;

    if (textAlign === "right") x = pageWidth - x;

    doc.setFont(fontName, fontStyle, fontWeight);
    doc.setFontSize(fontSize);
    doc.setTextColor(color);

    /** - clean up */
    text = text?.replace(/\r|\n|\t/gim, "");
    text = DOMPurify.sanitize(text, { ALLOWED_TAGS: ALLOWED_TAGS.map((t) => t.name), ALLOWED_ATTR });
    /** - /clean up */

    if (raw) {
        const _parsedValue = parsify({ doc, stringHTML: text, toSize: pageWidth - x * 2, startX: x, startY: y });
        y = printParsedValue({ doc, parsedValue: _parsedValue, toSize: pageWidth - x * 2, startX: x, startY: y });
        return y;
    } else {
        const _text = doc.splitTextToSize(text?.replace(/(<([^>]+)>)|\t|\r|\n/gi, ""), pageWidth - x * 2);

        console.log(` ------------`);
        console.log(`_text:`, _text);
        console.log(` ------------`);

        doc.text(_text, x, y, { align: textAlign });
        y += (_text.length * 3.78 * fontSize) / 10;
        return addLine({ yPos: y, doc });
    }
};

const parsify = ({ doc, stringHTML, toSize, startX, startY }) => {
    if (!doc || !stringHTML) return [];

    /** replace <b> to <strong> */
    stringHTML = stringHTML
        ?.replace(/<b>/gim, "<strong>")
        ?.replace(/<\/b>/gim, "</strong>")
        ?.replace(/<br>/gim, "<br> </br>")
        ?.replace(/<ul>/gim, "<ul> </ul>")
        ?.replace(/<ol>/gim, "<ol> </ol>");

    ALLOWED_TAGS.map((t) => t.name).forEach((tag) => {
        const _startPattern = `<${tag}>`;
        const _endPattern = `</${tag}>`;
        const _regEx = new RegExp(_startPattern + "|" + _endPattern, "gmi");
        stringHTML = stringHTML?.replace(_regEx, `{{${tag}}}`);
    });

    const _splitRegEx = new RegExp(
        ALLOWED_TAGS.filter((t) => t.newLine)
            .map((t) => `(?={{${t.name}}})`)
            ?.join("|"),
        "gmi"
    );

    const _splited = stringHTML?.split(_splitRegEx)?.filter(
        (f) =>
            !ALLOWED_TAGS.filter((t) => t.newLine)
                .map((t) => `{{${t.name}}}`)
                .includes(f)
    );

    const result = _splited?.flatMap((s) => doc.splitTextToSize(s, toSize));
    return result;
};

const printParsedValue = ({ doc, parsedValue, toSize, startX, startY, fontSize }) => {
    if (!doc || !parsedValue?.length) return;

    let textIndent = 0;
    let _x = startX;
    let _y = startY;
    let listOl = 0;

    parsedValue.forEach((STRING) => {
        STRING = STRING?.trim();

        if (!STRING || STRING === "{{p}}") return;

        // console.log(STRING)

        let _fontSize = FONT_SIZE * 1;
        let _fontStyle = "normal";
        let _fontWeight = 300;
        let _underline = false;
        let _isLink = false;
        let _links = {};
        let _linkID = 0;
        console.log({ STRING });

        /** Links */
        const _linkRegEx = /<\s*a\s*href="(.*?)"[^>]*>(.*?){{a}}/gim;
        let _linksResult;

        while ((_linksResult = _linkRegEx.exec(STRING)) !== null) {
            // This is necessary to avoid infinite loops with zero-width matches
            if (_linksResult.index === _linkRegEx.lastIndex) {
                _linkRegEx.lastIndex++;
            }

            _linkID++;
            const item = { value: _linksResult[2], href: _linksResult[1] };
            _links[_linkID] = item;
            STRING = STRING?.replace(_linksResult[0], `{{a}}${_linksResult[2]}|${_linkID}{{a}}`);
        }
        /** /Links */

        const _newLineRegEx = new RegExp(
            ALLOWED_TAGS.filter((t) => t.newLine)
                .map((t) => `{{${t.name}}}`)
                ?.join("|"),
            "gmi"
        );

        /** UL or OL */
        if (!!STRING.match(/{{ul}}/gim)?.length) {
            listOl = 0;
            _y -= LINE_HEIGHT;
        }
        if (!!STRING.match(/{{ol}}/gim)?.length) {
            listOl = 1;
        }

        /** Empty OL */
        if (STRING?.trim() === "{{ol}}") _y -= LINE_HEIGHT + 1.15;

        /** Indent string */
        if (!!STRING.match(/{{li}}/gim)?.length) {
            textIndent = textIndent || 5;
            _y += LINE_HEIGHT / 4;
            STRING = STRING.replace(/{{li}}/gim, listOl > 0 ? `${listOl}. ` : `• `);
            if (listOl > 0) {
                listOl++;
            } else {
                listOl = 0;
            }
        }

        if (!!STRING.match(_newLineRegEx)?.length && textIndent > 0) {
            textIndent = 0;
            _y += LINE_HEIGHT / 2;
        }

        /** Remove new lines  */
        STRING = STRING?.replace(_newLineRegEx, "");

        /** Minimize tags */
        STRING = STRING?.replace(/{{strong}}/gim, "{{*}}")
            ?.replace(/{{em}}/gim, "{{_}}")
            ?.replace(/{{sub}}/gim, "{{&}}")
            ?.replace(/{{sup}}/gim, "{{^}}");

        let _xx = _x + textIndent;
        let _yy = _y;
        let _str = "";
        let _strObj = [];

        for (let pos = 0; pos < STRING.length; pos++) {
            let _char = STRING[pos];

            let _tag = STRING.slice(pos, pos + 5);
            _tag = ["{{*}}", "{{_}}", "{{&}}", "{{^}}", "{{u}}", "{{a}}"].includes(_tag) ? _tag : false;

            if (_tag) {
                let _item = { _str, _fontSize, _fontStyle, _fontWeight, _xx, _yy, _underline, _isLink };
                _strObj.push(_item);
                _str = "";
                pos += 4;

                /** Bold */
                if (_tag === "{{*}}") {
                    if (_fontWeight > 300) {
                        /** end tag */
                        _fontWeight = 300;
                    } else {
                        /** start tag */
                        _fontWeight = 600;
                    }
                }

                /** Italic */
                if (_tag === "{{i}}") {
                    if (_fontStyle === "italic") {
                        /** end tag */
                        _fontStyle = "normal";
                    } else {
                        /** start tag */
                        _fontStyle = "italic";
                    }
                }

                /** SUB */
                if (_tag === "{{&}}") {
                    if (_fontSize < FONT_SIZE) {
                        /** end tag */
                        _fontSize = FONT_SIZE;
                        _yy = _y;
                    } else {
                        /** start tag */
                        _fontSize = FONT_SIZE * 0.9;
                        _yy = _y + LINE_HEIGHT / 4;
                    }
                }

                /** SUP */
                if (_tag === "{{^}}") {
                    if (_fontSize < FONT_SIZE) {
                        /** end tag */
                        _fontSize = FONT_SIZE;
                        _yy = _y;
                    } else {
                        /** start tag */
                        _fontSize = FONT_SIZE * 0.9;
                        _yy = _y - LINE_HEIGHT / 4;
                    }
                }

                /** Undeline */
                if (_tag === "{{u}}") {
                    if (_underline) {
                        /** end tag */
                        _underline = false;
                    } else {
                        /** start tag */
                        _underline = true;
                    }
                }

                /** Link */
                if (_tag === "{{a}}") {
                    if (_isLink) {
                        /** end tag */
                        _isLink = false;
                    } else {
                        /** start tag */
                        _isLink = true;
                    }
                }
            } else {
                _str = _str + _char;
            }
        }

        let _item = { _str, _fontSize, _fontStyle, _fontWeight, _xx, _yy, _underline, _isLink };
        if (!_strObj?.map((s) => s._str).includes(_str)) {
            _strObj.push(_item);
        }

        _xx = startX;
        _strObj.forEach((item, i) => {
            let _addX = 0;
            if (i > 0) {
                let _prevItem = _strObj[i - 1];
                doc.setFont("Open Sans", _prevItem._fontStyle, _prevItem._fontWeight);
                doc.setFontSize(_prevItem._fontSize);
                _addX = doc.getTextDimensions(_prevItem._str)?.w;
            }

            doc.setFont("Open Sans", item._fontStyle, item._fontWeight);
            doc.setFontSize(item._fontSize);
            _xx = _xx + _addX;

            if (item._isLink) {
                const _linkItem = _links[item._str?.split("|")[1]];
                doc.setTextColor(COLORS.link);
                doc.textWithLink(_linkItem?.value || "", _xx, item._yy, { align: "left", url: _linkItem?.href });

                let _from = _xx;
                let _to =
                    _xx +
                    doc.getTextDimensions(item._str)?.w -
                    doc.getTextDimensions(`|${item._str?.split("|")[1]}`)?.w;
                doc.setLineWidth(0.05);
                doc.setDrawColor(COLORS.link);
                doc.line(_from, item._yy + 0.5, _to, item._yy + 0.5);

                _xx = _xx - doc.getTextDimensions(`|${item._str?.split("|")[1]}`)?.w;
            } else {
                doc.setTextColor(COLORS.text);
                doc.text(item._str, _xx, item._yy, { align: "left" });
            }

            if (item._underline) {
                let _from = _xx;
                let _to = _xx + doc.getTextDimensions(item._str)?.w;
                doc.setLineWidth(0.05);
                doc.setDrawColor(33, 33, 33);
                doc.line(_from, item._yy + 0.5, _to, item._yy + 0.5);
            }
        });

        /** Clean string */
        const _cleanRegEx = new RegExp(
            [...ALLOWED_TAGS.map((t) => `{{${t.name}}}`), "{{*}}", "{{_}}", "{{&}}", "{{^}}"]?.join("|"),
            "gmi"
        );
        STRING = STRING?.replace(_cleanRegEx, "");
        _y += LINE_HEIGHT;
        _y = addLine({ yPos: _y, doc });
    });
    return _y;
};
